import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useProductsStore } from '../store/productsStore';
import { formatPrice } from '../data/products';

const CartDrawer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQuantity, clearCart, getCartTotal, generateOrderNumber } = useStore();
  const { shippingCost: SHIPPING_FEE, freeShippingMin: FREE_SHIPPING_THRESHOLD } = useProductsStore((state) => state.shipping);
  const [orderNumber, setOrderNumber] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const total = getCartTotal();
  const shipping = total >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const grandTotal = total + shipping;
  const freeShipping = total >= FREE_SHIPPING_THRESHOLD;

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/odeme');
  };

  if (!cartOpen) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setCartOpen(false); setShowSuccess(false); }} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-[#E31E24] to-[#FF4500]">
          <h2 className="text-white font-bold text-lg">🚗 {t('cart.title')}</h2>
          <button
            onClick={() => { setCartOpen(false); setShowSuccess(false); }}
            className="text-white/80 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {showSuccess ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">✅</span>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('cart.orderSuccess')}</h3>
            <p className="text-sm text-gray-500 mb-4">{t('cart.orderMessage')}</p>
            <div className="bg-gray-50 border-2 border-dashed border-[#E31E24] rounded-xl px-6 py-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">{t('cart.orderNumber')}</p>
              <p className="text-xl font-black text-[#E31E24] tracking-wider">{orderNumber}</p>
            </div>
            <button
              onClick={() => { setCartOpen(false); setShowSuccess(false); }}
              className="px-6 py-3 bg-[#E31E24] text-white font-bold rounded-xl hover:bg-[#c41a1f] transition-colors"
            >
              {t('cart.continueShopping')}
            </button>
          </div>
        ) : cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="text-6xl mb-4">🛒</div>
            <p className="text-gray-500">{t('cart.empty')}</p>
          </div>
        ) : (
          <>
            {/* Free Shipping Banner */}
            {freeShipping && (
              <div className="bg-green-50 text-green-700 text-xs font-medium text-center py-2 px-4">
                🎉 {FREE_SHIPPING_THRESHOLD} TL ve üzeri alışverişlerinizde ücretsiz kargo!
              </div>
            )}

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.map((item) => {
                const price = item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice;
                return (
                  <div key={item.product.id} className="flex gap-3 bg-gray-50 rounded-xl p-3">
                    <img
                      src={item.product.images[0]}
                      alt={item.product.name}
                      className="w-16 h-16 object-contain rounded-lg bg-white shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-semibold text-gray-800 line-clamp-2">{item.product.name}</h4>
                      <p className="text-sm font-bold text-[#E31E24] mt-1">
                        {price > 0 ? `${formatPrice(price)} ₺` : 'Fiyat bilgisi yok'}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 text-sm font-bold flex items-center justify-center"
                        >
                          −
                        </button>
                        <span className="text-sm font-semibold w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-6 h-6 rounded-full bg-[#E31E24] hover:bg-[#c41a1f] text-white text-sm font-bold flex items-center justify-center"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product.id)}
                      className="text-gray-400 hover:text-[#E31E24] text-lg leading-none self-start"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Ara Toplam</span>
                <span className="font-semibold">{formatPrice(total)} ₺</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Kargo</span>
                <span className={`font-semibold ${shipping === 0 ? 'text-green-600' : ''}`}>
                  {shipping === 0 ? 'Ücretsiz' : `${formatPrice(shipping)} ₺`}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                <span className="text-sm font-bold text-gray-800">Toplam</span>
                <span className="text-xl font-black text-[#E31E24]">{formatPrice(grandTotal)} ₺</span>
              </div>
              {!freeShipping && (
                <p className="text-xs text-[#E31E24] text-center">
                  🎉 {FREE_SHIPPING_THRESHOLD} TL ve üzeri alışverişlerde ücretsiz kargo!
                </p>
              )}
              <button
                onClick={handleCheckout}
                className="w-full py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all uppercase tracking-wider"
              >
                🚗 {t('cart.checkout')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
